/**
 * Mobile Tabs Dropdown
 * @module components/layout/tabs/MobileTabs
 */

import { DISABLED_REASONS } from "@lib/tabs/config";
import type { DisabledReason, Tab } from "@lib/tabs/types";
import { Link } from "@tanstack/react-router";
import { Check, ChevronDown } from "lucide-react";
import { useState } from "react";

interface MobileTabsProps {
  tabs: Tab[];
}

/**
 * Mobile dropdown tabs — matches the unified compact design
 */
export function MobileTabs({ tabs }: MobileTabsProps) {
  const [isOpen, setIsOpen] = useState(false);

  const activeTab = tabs.find((t) => t.isActive) ?? tabs[0];
  const ActiveIcon = activeTab?.icon;

  return (
    <div className="dropdown w-full" onKeyDown={(e) => e.key === "Escape" && setIsOpen(false)}>
      <button
        type="button"
        tabIndex={0}
        className="mobile-tab-trigger"
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <span className="flex items-center gap-2">
          {ActiveIcon && <ActiveIcon className="h-4 w-4" />}
          <span>{activeTab?.title ?? "Sélectionner"}</span>
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>

      {isOpen && (
        <ul
          role="menu"
          className="dropdown-content z-10 menu p-2 shadow-lg bg-base-100 rounded-box w-full border border-base-300"
        >
          {tabs.map((tab) => {
            const TabIcon = tab.icon;

            return (
              <li key={tab.id} role="menuitem">
                {tab.isDisabled ? (
                  <span
                    className="opacity-50 cursor-not-allowed px-4 py-2.5 text-sm flex items-center gap-2"
                    title={
                      tab.disabledReason
                        ? DISABLED_REASONS[tab.disabledReason as DisabledReason]
                        : undefined
                    }
                  >
                    {TabIcon && <TabIcon className="h-4 w-4 text-base-content/50" />}
                    <span>{tab.title}</span>
                  </span>
                ) : (
                  <Link
                    to={tab.path as "/" | `/${string}`}
                    className={[
                      "px-4 py-2.5 text-sm flex items-center gap-2 rounded-md",
                      "hover:bg-base-200 transition-colors duration-100",
                      tab.isActive ? "bg-primary/10 text-primary font-medium" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => setIsOpen(false)}
                  >
                    {TabIcon && <TabIcon className="h-4 w-4 text-base-content/50" />}
                    <span className="flex-1">{tab.title}</span>
                    {tab.isActive && <Check className="h-4 w-4 text-primary" />}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
